// MiniPattern → Strudel mini-notation string.
//
// Deterministic — the same tree always produces the same string. Pair
// with parseMini for round-trip invariants: parse(serialize(t)) ≡ t
// (up to block ids, which are regenerated).

import type { MiniNode, MiniPattern, Numeric } from '../types';

export function serializeMini(nodes: MiniPattern): string {
  return nodes.map(serializeNode).join(' ');
}

function serializeNode(node: MiniNode): string {
  switch (node.kind) {
    case 'atom':
      return node.index === undefined
        ? node.name
        : `${node.name}:${node.index}`;
    case 'rest':
      return '~';
    case 'repeat':
      return `${serializeNode(node.inner)}*${serializeNumeric(node.count)}`;
    case 'slow':
      return `${serializeNode(node.inner)}/${serializeNumeric(node.factor)}`;
    case 'bang':
      return `${serializeNode(node.inner)}!${serializeNumeric(node.count)}`;
    case 'weight':
      return `${serializeNode(node.inner)}@${serializeNumeric(node.weight)}`;
    case 'euclid': {
      const parts = [serializeNumeric(node.pulses), serializeNumeric(node.steps)];
      if (node.rot !== undefined) parts.push(serializeNumeric(node.rot));
      return `${serializeNode(node.inner)}(${parts.join(',')})`;
    }
    case 'group':
      return `[${node.children.map(serializeNode).join(' ')}]`;
    case 'choice':
      return `<${node.children.map(serializeNode).join(' ')}>`;
    case 'parallel':
      return `[${node.children.map(serializeTrack).join(', ')}]`;
    case 'polymeter': {
      const inner = node.children.map(serializeTrack).join(', ');
      const steps = node.steps === undefined ? '' : `%${serializeNumeric(node.steps)}`;
      return `{${inner}}${steps}`;
    }
  }
}

// Inside a parallel/polymeter track, unwrap a group so that
// `[a b, c d]` serializes as `[a b, c d]` not `[[a b], [c d]]`.
function serializeTrack(node: MiniNode): string {
  if (node.kind === 'group') {
    return node.children.map(serializeNode).join(' ');
  }
  return serializeNode(node);
}

function serializeNumeric(n: Numeric): string {
  switch (n.kind) {
    case 'num':
      return String(n.value);
    case 'binary':
      return `${serializeNumeric(n.lhs)}${n.op}${serializeNumeric(n.rhs)}`;
    case 'ref':
      return n.name;
    case 'raw':
      return n.source;
    case 'modulator':
      // Modulators can't actually appear inside a mini-notation
      // numeric (Strudel's grammar rejects them there), but we keep
      // the case total by emitting the source verbatim.
      return n.source;
  }
}
